import { APP_ROUTER } from '@/routes/routes';
import { Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Text, Paragraph } = Typography;

const C = {
  llm: { fill: '#f9f0ff', stroke: '#b37feb', text: '#531dab' }, // Anthropic
  img: { fill: '#f6ffed', stroke: '#95de64', text: '#389e0d' }, // OpenAI 画像
  local: { fill: '#fafafa', stroke: '#d9d9d9', text: '#595959' }, // ローカル処理
  db: { fill: '#e6f4ff', stroke: '#69b1ff', text: '#0958d9' }, // 保存
};

type StepKind = 'llm' | 'img' | 'local' | 'db';
type Step = {
  n: string;
  kind: StepKind;
  title: string;
  sub: string;
  promptKeys?: string[];
  /** カバー画像系（skipCover でスキップ）= 破線枠 */
  skippable?: boolean;
  /** 所属ブロック: shared / morning / evening */
  block: 'shared' | 'morning' | 'evening';
  /** family テーマの日だけ挙動が変わるステップに付けるバッジ */
  familyBadge?: string;
};

const STEPS: Step[] = [
  {
    n: '1',
    kind: 'llm',
    block: 'shared',
    title: 'テーマ選定',
    sub: 'claude-opus-4-7 ・ Web検索あり（漫才と共有・1日1回）',
    promptKeys: ['theme.user'],
  },
  // --- 朝刊 ---
  {
    n: '2',
    kind: 'llm',
    block: 'morning',
    title: 'ファクトチェック',
    sub: 'claude-sonnet-4-6 ・ Web検索あり',
    promptKeys: ['article.factcheck.user'],
    familyBadge: 'familyの日はスキップ',
  },
  {
    n: '3',
    kind: 'llm',
    block: 'morning',
    title: '本文を生成',
    sub: 'claude-opus-4-7 ・ familyの日は override を system に連結',
    promptKeys: [
      'article.morning.system',
      'article.morning.user',
      'article.family.override',
    ],
    familyBadge: 'familyの日は情緒モード',
  },
  {
    n: '4',
    kind: 'llm',
    block: 'morning',
    title: 'サムネ用プロンプト生成',
    sub: 'claude-sonnet-4-6',
    promptKeys: ['thumb.article.system', 'thumb.article.user'],
    skippable: true,
  },
  {
    n: '5',
    kind: 'local',
    block: 'morning',
    title: '参照画像を取得',
    sub: 'LLMなし ・ フルマルの avatar 画像を取得',
    skippable: true,
  },
  {
    n: '6',
    kind: 'img',
    block: 'morning',
    title: 'カバー画像を生成',
    sub: 'gpt-image-2 (images.edit) ・ 参照画像（フルマル）+ プロンプト',
    skippable: true,
  },
  {
    n: '7',
    kind: 'db',
    block: 'morning',
    title: 'DB に保存（朝刊）',
    sub: 'status = published',
  },
  // --- 夕刊 ---
  {
    n: '8',
    kind: 'llm',
    block: 'evening',
    title: 'ファクトチェック',
    sub: 'claude-sonnet-4-6 ・ Web検索あり',
    promptKeys: ['article.factcheck.user'],
    familyBadge: 'familyの日はスキップ',
  },
  {
    n: '9',
    kind: 'llm',
    block: 'evening',
    title: '本文を生成（朝刊本文を文脈に）',
    sub: 'claude-opus-4-7 ・ familyの日は override を system に連結',
    promptKeys: [
      'article.evening.system',
      'article.evening.user',
      'article.evening.morning_context',
      'article.family.override',
    ],
    familyBadge: 'familyの日は情緒モード',
  },
  {
    n: '10',
    kind: 'llm',
    block: 'evening',
    title: 'サムネ用プロンプト生成',
    sub: 'claude-sonnet-4-6',
    promptKeys: ['thumb.article.system', 'thumb.article.user'],
    skippable: true,
  },
  {
    n: '11',
    kind: 'local',
    block: 'evening',
    title: '参照画像を取得',
    sub: 'LLMなし ・ フルマルの avatar 画像を取得',
    skippable: true,
  },
  {
    n: '12',
    kind: 'img',
    block: 'evening',
    title: 'カバー画像を生成',
    sub: 'gpt-image-2 (images.edit) ・ 参照画像（フルマル）+ プロンプト',
    skippable: true,
  },
  {
    n: '13',
    kind: 'db',
    block: 'evening',
    title: 'DB に保存（夕刊）',
    sub: 'status = published',
  },
];

const VIEW_W = 800;
const BOX_X = 120;
const BOX_W = 560;
const BOX_H = 76;
const GAP = 28;
const TOP = 16;
const stepPitch = BOX_H + GAP;

const boxY = (i: number) => TOP + i * stepPitch;
const VIEW_H = boxY(STEPS.length - 1) + BOX_H + 16;

const firstIndexOfBlock = (b: Step['block']) =>
  STEPS.findIndex((s) => s.block === b);
const lastIndexOfBlock = (b: Step['block']) => {
  for (let i = STEPS.length - 1; i >= 0; i--) if (STEPS[i].block === b) return i;
  return -1;
};

export const ArticleFlowDiagram: React.FC = () => {
  const navigate = useNavigate();
  const goPrompt = (key: string) =>
    navigate(`${APP_ROUTER.KAZOKUGOTO.PROMPTS}?key=${encodeURIComponent(key)}`);

  const morningBand = {
    y1: boxY(firstIndexOfBlock('morning')) - 8,
    y2: boxY(lastIndexOfBlock('morning')) + BOX_H + 8,
  };
  const eveningBand = {
    y1: boxY(firstIndexOfBlock('evening')) - 8,
    y2: boxY(lastIndexOfBlock('evening')) + BOX_H + 8,
  };

  return (
    <div>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        width='100%'
        style={{ maxWidth: VIEW_W, fontFamily: 'inherit' }}
        role='img'
        aria-label='朝刊・夕刊の生成フロー図'
      >
        <defs>
          <marker
            id='a-arrow'
            markerWidth='10'
            markerHeight='10'
            refX='6'
            refY='3'
            orient='auto'
            markerUnits='strokeWidth'
          >
            <path d='M0,0 L6,3 L0,6 Z' fill='#8c8c8c' />
          </marker>
          <marker
            id='a-arrow-ctx'
            markerWidth='10'
            markerHeight='10'
            refX='6'
            refY='3'
            orient='auto'
            markerUnits='strokeWidth'
          >
            <path d='M0,0 L6,3 L0,6 Z' fill='#1677ff' />
          </marker>
        </defs>

        {/* 朝刊 / 夕刊 グルーピング帯（box の背面） */}
        <g>
          <rect
            x={BOX_X - 12}
            y={morningBand.y1}
            width={BOX_W + 24}
            height={morningBand.y2 - morningBand.y1}
            rx={10}
            fill='#fffbe6'
            stroke='#ffe58f'
          />
          <text
            x={BOX_X - 2}
            y={morningBand.y1 - 4}
            fontSize='13'
            fontWeight='bold'
            fill='#d48806'
          >
            朝刊（親世代向け）
          </text>
          <rect
            x={BOX_X - 12}
            y={eveningBand.y1}
            width={BOX_W + 24}
            height={eveningBand.y2 - eveningBand.y1}
            rx={10}
            fill='#f0f5ff'
            stroke='#adc6ff'
          />
          <text
            x={BOX_X - 2}
            y={eveningBand.y1 - 4}
            fontSize='13'
            fontWeight='bold'
            fill='#2f54eb'
          >
            夕刊（子世代向け）
          </text>
        </g>

        {/* 縦の接続矢印 */}
        {STEPS.slice(0, -1).map((_, i) => {
          const y1 = boxY(i) + BOX_H;
          const y2 = boxY(i + 1);
          return (
            <line
              key={`arr-${i}`}
              x1={BOX_X + BOX_W / 2}
              y1={y1}
              x2={BOX_X + BOX_W / 2}
              y2={y2 - 2}
              stroke='#8c8c8c'
              strokeWidth={1.5}
              markerEnd='url(#a-arrow)'
            />
          );
        })}

        {/* 朝刊本文(3) → 夕刊本文(8) morning_context 矢印（右回り込み） */}
        {(() => {
          const fromIdx = 2; // step3 朝刊本文
          const toIdx = 8; // step9 夕刊本文
          const fromY = boxY(fromIdx) + BOX_H / 2;
          const toY = boxY(toIdx) + BOX_H / 2;
          const rightX = BOX_X + BOX_W + 32;
          return (
            <g>
              <path
                d={`M ${BOX_X + BOX_W} ${fromY} L ${rightX} ${fromY} L ${rightX} ${toY} L ${BOX_X + BOX_W + 2} ${toY}`}
                fill='none'
                stroke='#1677ff'
                strokeWidth={1.5}
                strokeDasharray='5 3'
                markerEnd='url(#a-arrow-ctx)'
              />
              <text
                x={rightX + 6}
                y={(fromY + toY) / 2}
                fontSize='11'
                fill='#1677ff'
                textAnchor='middle'
                transform={`rotate(90 ${rightX + 6} ${(fromY + toY) / 2})`}
              >
                朝刊本文を文脈に渡す（morning_context）
              </text>
            </g>
          );
        })()}

        {/* 各ステップのボックス */}
        {STEPS.map((s, i) => {
          const y = boxY(i);
          const c = C[s.kind];
          return (
            <g key={s.n}>
              <rect
                x={BOX_X}
                y={y}
                width={BOX_W}
                height={BOX_H}
                rx={8}
                fill={c.fill}
                stroke={c.stroke}
                strokeWidth={1.5}
                strokeDasharray={s.skippable ? '6 3' : undefined}
              />
              <circle cx={BOX_X + 26} cy={y + BOX_H / 2} r={13} fill={c.stroke} />
              <text
                x={BOX_X + 26}
                y={y + BOX_H / 2 + 4}
                fontSize='13'
                fill='#fff'
                textAnchor='middle'
                fontWeight='bold'
              >
                {s.n}
              </text>
              <text
                x={BOX_X + 50}
                y={y + 24}
                fontSize='14'
                fill={c.text}
                fontWeight='bold'
              >
                {s.title}
              </text>
              <text x={BOX_X + 50} y={y + 44} fontSize='11.5' fill='#595959'>
                {s.sub}
              </text>
              {s.familyBadge &&
                (() => {
                  // 全角 ≈ 10.5px / 半角 ≈ 6px で幅を概算
                  const w =
                    Array.from(s.familyBadge).reduce(
                      (acc, ch) => acc + (ch.charCodeAt(0) > 0xff ? 10.5 : 6),
                      0,
                    ) + 16;
                  return (
                    <g>
                      <rect
                        x={BOX_X + BOX_W - w - 10}
                        y={y + 8}
                        width={w}
                        height={18}
                        rx={9}
                        fill='#fff0f6'
                        stroke='#ff85c0'
                      />
                      <text
                        x={BOX_X + BOX_W - 10 - w / 2}
                        y={y + 21}
                        fontSize='10.5'
                        fill='#c41d7f'
                        textAnchor='middle'
                      >
                        {s.familyBadge}
                      </text>
                    </g>
                  );
                })()}
              {s.promptKeys && (
                <text x={BOX_X + 50} y={y + 64} fontSize='12'>
                  <tspan fill='#8c8c8c'>prompt: </tspan>
                  {s.promptKeys.map((k, ki) => (
                    <tspan key={k}>
                      {ki > 0 && <tspan fill='#bfbfbf'> + </tspan>}
                      <tspan
                        fill='#1677ff'
                        style={{ cursor: 'pointer' }}
                        textDecoration='underline'
                        onClick={() => goPrompt(k)}
                      >
                        {k}
                      </tspan>
                    </tspan>
                  ))}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* 凡例 */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
        <LegendItem color={C.llm.stroke} label='Anthropic（テキスト生成）' />
        <LegendItem color={C.img.stroke} label='OpenAI（画像生成）' />
        <LegendItem color={C.local.stroke} label='ローカル処理 / DB 保存' />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              border: '1.5px dashed #b37feb',
              display: 'inline-block',
            }}
          />
          <Text style={{ fontSize: 12 }}>破線枠 = skipCover でスキップ</Text>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 7,
              background: '#fff0f6',
              border: '1px solid #ff85c0',
              display: 'inline-block',
            }}
          />
          <Text style={{ fontSize: 12 }}>
            ピンクのバッジ = family テーマの日だけ挙動が変わる
          </Text>
        </span>
      </div>

      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        青字のプロンプト key をクリックすると、その履歴・編集画面が開きます。
        朝刊→夕刊は直列で、夕刊は朝刊の本文を文脈に取って書きます。
      </Paragraph>

      <Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
        <Text strong>1日あたりの LLM 呼び出し（記事分・朝刊+夕刊）:</Text>
        <ul style={{ marginTop: 4, marginBottom: 0 }}>
          <li>claude-opus-4-7 … テーマ選定 ×1（漫才と共有）＋ 本文生成 ×2（朝刊・夕刊、各リトライ最大2回）</li>
          <li>claude-sonnet-4-6 … ファクトチェック ×2（family テーマの日は ×0）＋ サムネ用プロンプト ×2</li>
          <li>gpt-image-2 … カバー画像 ×2</li>
        </ul>
      </Paragraph>
      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        ※ <Text strong>family テーマの日（365日マスタで theme=family、年24日）</Text>
        は情緒・記憶ベースの家族記事のため一部ロジックが変わります:
        ①Web検索ファクトチェックをスキップ ②本文生成の system に
        article.family.override（記憶・沈黙・小さなアクションで終わる等の上書きルール）を連結
        ③source / ayumi_id は必ず null。サムネ・カバー画像の生成は通常と同じです。
      </Paragraph>
      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        ※ 漫才と違い、記事には台本の品質検証ステップはありません（JSON
        パース失敗時のみ最大2回リトライ）。カバー画像は漫才と同様に
        フルマルの avatar を参照画像にした images.edit で生成します
        （フルマルの id は環境変数 KAZOKUGOTO_FUKUMARU_CHARACTER_ID で指定・既定
        15。avatar が無い場合は参照なしの images.generate にフォールバック）。
      </Paragraph>
      <Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
        ※ 「生成する」ボタンを押すと、朝刊・夕刊・漫才を含む一連の処理は
        <Text strong>バックグラウンドジョブ</Text>として実行されます（数分かかるため）。
        生成ページはジョブの状態をポーリングし、完了で結果サマリを表示します。
        カバー画像も非同期で生成され、アイテム一覧では coverStatus が
        「生成中 → 生成済み / 失敗」と遷移します。
      </Paragraph>
    </div>
  );
};

const LegendItem: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: color,
        display: 'inline-block',
      }}
    />
    <Text style={{ fontSize: 12 }}>{label}</Text>
  </span>
);
